import { useEffect, useRef } from "react";

interface DeepSpaceParticlesProps {
  activeFrequency?: number;
  themeId?: string;
}

export default function DeepSpaceParticles({ activeFrequency, themeId }: DeepSpaceParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frequencyRef = useRef<number | undefined>(activeFrequency);

  useEffect(() => {
    frequencyRef.current = activeFrequency;
  }, [activeFrequency]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Definition of star types
    interface Star {
      x: number;
      y: number;
      size: number;
      baseColor: string;
      alpha: number;
      speedX: number;
      speedY: number;
      twinkleSpeed: number;
      phase: number;
      isTurquoise: boolean;
      isPurple: boolean;
    }

    const parseHexToRgba = (hex: string) => {
      let cleanHex = hex.trim().replace("#", "");
      if (cleanHex.length === 3) {
        cleanHex = cleanHex.split("").map(c => c + c).join("");
      }
      const r = parseInt(cleanHex.substring(0, 2), 16);
      const g = parseInt(cleanHex.substring(2, 4), 16);
      const b = parseInt(cleanHex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b},`;
    };

    const turquoiseHex = "#00D4FF";
    const purpleHex = "#A67CFF";
    const blueHex = "#4A90E2";
    const lightHex = "#7FB8FF";

    const tRgba = parseHexToRgba(turquoiseHex);
    const pRgba = parseHexToRgba(purpleHex);
    const bRgba = parseHexToRgba(blueHex);
    const lRgba = parseHexToRgba(lightHex);

    const starPalette = [
      { color: "rgba(255, 255, 255,", isT: false, isP: false }, // Pure White
      { color: tRgba, isT: true, isP: false },                  // Pleiadian Turquoise/High energy
      { color: pRgba, isT: false, isP: true },                  // Ethereal Violet/Mystic purple
      { color: bRgba, isT: false, isP: false },                 // Cosmic Blue
      { color: lRgba, isT: false, isP: false }                  // Soft Light Blue
    ];

    const stars: Star[] = [];
    const starCount = 100; // Optimal density for visual appeal and performance

    // Initialize stars at random positions
    for (let i = 0; i < starCount; i++) {
      const paletteItem = starPalette[Math.floor(Math.random() * starPalette.length)];
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 2.2 + 0.4, // Twinkling stars of varying sizes
        baseColor: paletteItem.color,
        isTurquoise: paletteItem.isT,
        isPurple: paletteItem.isP,
        alpha: Math.random() * 0.7 + 0.1,
        speedX: (Math.random() - 0.5) * 0.08 - 0.05, // Gentle leftward/upward movement
        speedY: (Math.random() - 0.5) * 0.05 - 0.02, // Gentle vertical drift
        twinkleSpeed: Math.random() * 0.01 + 0.003,
        phase: Math.random() * Math.PI * 2
      });
    }

    // Scroll tracking for parallax depth & velocity
    let lastScrollY = window.scrollY;
    let smoothScrollY = window.scrollY;

    // Handle responsiveness
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    const animate = () => {
      // Clear background completely
      ctx.clearRect(0, 0, width, height);

      // Smoothly interpolate scroll position for lag-free parallax
      smoothScrollY += (window.scrollY - smoothScrollY) * 0.08;
      const scrollVelocity = Math.abs(window.scrollY - smoothScrollY);
      // Generate a speed boost when the user is actively scrolling
      const scrollSpeedBoost = Math.min(3.0, scrollVelocity * 0.06);

      // Calculate dynamic speed multiplier based on active frequency
      // Default multiplier is 1.0 when no frequency is active.
      let speedMultiplier = 1.0;
      if (frequencyRef.current) {
        const freq = frequencyRef.current;
        // Map 285Hz -> ~1.8x speed, 963Hz -> ~5.2x speed
        speedMultiplier = 1.0 + ((freq - 150) / 180);
        speedMultiplier = Math.max(1.0, Math.min(6.5, speedMultiplier));
      }

      const totalSpeedMultiplier = speedMultiplier + scrollSpeedBoost;

      // Render starfield
      stars.forEach((star) => {
        // Apply slow-moving drift scaled by dynamic speed multiplier & active scroll warp speed
        star.x += star.speedX * totalSpeedMultiplier;
        star.y += star.speedY * totalSpeedMultiplier;

        // Wrap around screen boundaries
        if (star.x < 0) star.x = width;
        if (star.x > width) star.x = 0;
        if (star.y < 0) star.y = height;
        if (star.y > height) star.y = 0;

        // Calculate Y coordinate with scroll parallax based on star size (acting as depth indicator)
        // Larger stars are closer and shift more, smaller stars are further and shift less.
        let renderY = (star.y - smoothScrollY * (star.size * 0.12)) % height;
        if (renderY < 0) renderY += height;

        // Update twinkle phase for smooth glowing transitions, scaled by speed
        star.phase += star.twinkleSpeed * (0.5 + totalSpeedMultiplier * 0.5);
        const currentAlpha = Math.max(0.1, Math.min(0.9, star.alpha + Math.sin(star.phase) * 0.3));

        // Draw individual star with optional size pulsing when speed is high
        const pulseFactor = frequencyRef.current ? (1.0 + Math.sin(star.phase * 2) * 0.15) : 1.0;
        const currentSize = star.size * pulseFactor;

        ctx.beginPath();
        ctx.arc(star.x, renderY, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `${star.baseColor}${currentAlpha})`;

        // Apply a glowing drop shadow for Pleiadian turquoise/purple stars (amplified when frequency active)
        if (star.size > 1.6 && (star.isTurquoise || star.isPurple)) {
          ctx.shadowBlur = frequencyRef.current ? 12 : 6;
          ctx.shadowColor = star.isTurquoise ? turquoiseHex : purpleHex;
        } else {
          ctx.shadowBlur = 0;
        }

        ctx.fill();
      });

      // Reset shadows
      ctx.shadowBlur = 0;

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [themeId]);

  return (
    <canvas
      ref={canvasRef}
      id="space-particles-canvas"
      className="fixed inset-0 pointer-events-none z-0 opacity-45"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
